/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime, fromEvent, map, Subscription } from 'rxjs';

@Component({
  selector: 'umss-textarea',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssTextareaComponent),
      multi: true
    }
  ]
})
export class UmssTextareaComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('textarea') textareaRef!: ElementRef<HTMLTextAreaElement>;

  readonly inputId = `umss-textarea-${Math.random().toString(36).slice(2)}`;

  constructor() {}

  ngOnDestroy(): void {
    this.scrollSub?.unsubscribe();
  }

  /** Content props */
  @Input() label?: string;
  @Input() placeholder: string = '';
  @Input() helperText?: string;
  @Input() errorMessage?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() rows: number = 4;
  @Input() maxLength?: number;
  @Input() resize: 'none' | 'vertical' | 'horizontal' | 'both' = 'vertical';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() error: boolean = false;

  /** Value prop */
  private _value: string = '';
  @Input() set value(val: string) {
    this._value = val;
  }
  get value(): string {
    return this._value;
  }

  /** Event outputs */
  @Output() valueChange = new EventEmitter<string>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() onInput = new EventEmitter<string>();
  @Output() onScroll = new EventEmitter<number>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};
  private scrollSub?: Subscription;

  ngAfterViewInit() {
    this.subscribeToScroll();
  }

  writeValue(value: string): void {
    this._value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onTextareaInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    let value = target.value;

    // Manual maxLength validation
    if (this.maxLength && value.length > this.maxLength) {
      value = value.substring(0, this.maxLength);
      target.value = value;
    }

    this._value = value;
    this._onChange(this._value);
    this.valueChange.emit(this._value);
    this.onInput.emit(this._value);
  }

  onTextareaFocus(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onTextareaBlur(): void {
    if (this.disabled) return;
    this._onTouched();
    this.onBlur.emit();
  }

  /** Computed properties */
  get hasValue(): boolean {
    return !!(this._value && this._value.length > 0);
  }

  get characterCount(): string {
    if (!this.maxLength) return '';
    return `${this._value.length}/${this.maxLength}`;
  }

  private onScrollEvent(textarea: HTMLTextAreaElement): number {
    const scrollTop = textarea.scrollTop;
    const scrollHeight = textarea.scrollHeight - textarea.clientHeight;

    return Math.round((scrollTop / scrollHeight) * 100);
  }

  private subscribeToScroll(): void {
    const textarea = this.textareaRef.nativeElement;

    this.scrollSub = fromEvent(textarea, 'scroll')
      .pipe(
        debounceTime(100),
        map(() => this.onScrollEvent(textarea))
      )
      .subscribe((percent) => {
        this.onScroll.emit(percent);
      });
  }
}
